import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import SearshFilter from '../components/shared/SearshFilter'
import showSingelCard from '../apis/showSingelCard'
import CardDetailes from '../components/card/CardDetailes'
import showCardPackages from '../apis/showCardPackages'
import PackageCard from '../components/package/PackageCard'

const SingelCard = () => {
  const { cardId } = useParams()
  const navigate = useNavigate()
  const [filter, setFilter] = useState('All')
  const [stockStatus, setStockStatus] = useState('all')
  const [sortType, setSortType] = useState('creationDate')
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [card, setCard] = useState([])
  const [packages, setPackages] = useState([])
  const [filteredPackages, setFilteredPackages] = useState([])
  const [displayedPackages, setDisplayedPackages] = useState([])

  const itemsPerPage = 3

  const fetchItems = useCallback(async () => {
    setLoading(true)
    try {
      const cardData = await showSingelCard(cardId)
      const combinedCategories = cardData.categories.map(
        (categoryId, index) => ({
          id: categoryId,
          name: cardData.category_names[index],
        })
      )
      setCard({ ...cardData, categories: combinedCategories })

      const cardPackages = await showCardPackages(cardId)
      setPackages(cardPackages)
    } catch (error) {
      setPackages([])
    } finally {
      setLoading(false)
    }
  }, [cardId])

  useEffect(() => {
    fetchItems()
  }, [fetchItems])

  const refresh = () => {
    fetchItems()
  }

  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, filter, sortType])

  useEffect(() => {
    // Update filtered packages based on the current filters
    const filtered = packages?.filter((item) => {
      const matchesFilter =
        filter === 'All' ||
        (filter === 'Active' && item.is_active === 1) ||
        (filter === 'Inactive' && item.is_active === 0)
      const matchesSearch = item.title
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
      const matchesStockStatus =
        stockStatus === 'all' ||
        (stockStatus === 'inStock' && item.out_of_stock === 0) ||
        (stockStatus === 'outOfStock' && item.out_of_stock === 1)
      return matchesFilter && matchesSearch && matchesStockStatus
    })

    // Sort the filtered packages
    const sorted = filtered?.sort((a, b) => {
      if (sortType === 'alphabet') {
        return a.title.localeCompare(b.title)
      } else if (sortType === 'creationDate') {
        return new Date(b.created_at) - new Date(a.created_at)
      }
      return 0
    })

    setFilteredPackages(sorted)
  }, [packages, filter, searchTerm, sortType, stockStatus])

  useEffect(() => {
    // Update displayed packages for the current page
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    const displayed = filteredPackages?.slice(startIndex, endIndex)
    setDisplayedPackages(displayed)
  }, [currentPage, filteredPackages])

  const handleClick = (pageNumber) => {
    setCurrentPage(pageNumber)
  }

  const totalPages = Math.ceil(filteredPackages.length / itemsPerPage)

  const updatePackage = (updatedPackage) => {
    setPackages((prevPackages) =>
      prevPackages.map((pack) => {
        if (pack.id === updatedPackage.id) {
          const normalizedPackage = {
            ...pack, // Keep other existing properties
            title: updatedPackage.title,
            image: updatedPackage.image,
            count: updatedPackage.quantity || updatedPackage.count, // Map `quantity` to `count`
            is_active:
              updatedPackage.isActive !== undefined
                ? updatedPackage.isActive
                : updatedPackage.is_active, // Map `isActive` to `is_active`
            max_number_to_buy_at_once:
              updatedPackage.maxNumberToBuyAtOnce ||
              updatedPackage.max_number_to_buy_at_once,
            serial_number:
              updatedPackage.serialNumber || updatedPackage.serial_number,
            price_pos_client:
              updatedPackage.pricePosClient || updatedPackage.price_pos_client,
            price_client:
              updatedPackage.priceClient || updatedPackage.price_client,
            out_of_stock:
              updatedPackage.outOfStock !== undefined
                ? updatedPackage.outOfStock
                  ? 1
                  : 0
                : updatedPackage.out_of_stock,
            updated_at: updatedPackage.updated_at || new Date().toISOString(), // Update the timestamp
          }

          return normalizedPackage
        }
        return pack
      })
    )
  }

  const removePackage = (packageId) => {
    setPackages((prevPackages) =>
      prevPackages.filter((pack) => pack.id !== packageId)
    )

    // Update pagination if the last item on the page was removed
    if (displayedPackages.length === 1 && currentPage > 1) {
      setCurrentPage((prevPage) => prevPage - 1)
    }
    fetchItems()
  }

  const handleCategoryChange = (selectedCategory) => {
    if (selectedCategory) {
      navigate(
        `/cards?category_id=${selectedCategory.id}&name=${selectedCategory.title}`
      )
    } else {
      navigate('/cards')
    }
  }

  const handleGoBack = () => {
    if (window.history.length > 2) {
      navigate(-1) // Go back one page in history
    }
  }

  return (
    <>
      <div className=" w-full flex justify-between items-center -mb-4">
        <div className="text-2xl text-blueButton">{`  ${card.title}`}</div>
        <div>
          {window.history.length > 2 && (
            <button
              className=" bg-blueButton  rounded shadow-xl text-white text-xl px-5 py-2"
              onClick={handleGoBack}
            >
              go back
            </button>
          )}
        </div>
      </div>
      <SearshFilter
        type="packages"
        filter={filter}
        setFilter={setFilter}
        sortType={sortType}
        setSortType={setSortType}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        onCategoryChange={handleCategoryChange}
        stockStatus={stockStatus}
        setStockStatus={setStockStatus}
        refresh={refresh}
      />
      <div className="h-full shadow rounded-xl pb-2 pt-2 px-4 flex flex-col justify-between">
        <div className="w-full flex flex-wrap gap-3 items-center">
          {loading ? (
            'loading...'
          ) : (
            <>
              <CardDetailes card={card} categoryNames={[]} />
              <div className="flex w-full justify-center pt-10 items-center">
                <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {!packages || packages?.length === 0 ? (
                    <div>No packages yet</div>
                  ) : (
                    <>
                      {displayedPackages?.length === 0 ? (
                        <div>No matching packages</div>
                      ) : (
                        displayedPackages?.map((pack, index) => (
                          <PackageCard
                            key={index}
                            card={card}
                            pack={pack}
                            index={index}
                            removePackage={removePackage}
                            updatePackage={updatePackage}
                          />
                        ))
                      )}
                    </>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
        <div className="flex gap-8 mt-4">
          <div className="text-gray">Page:</div>
          <div className="text-gray flex gap-4">
            {Array.from({ length: totalPages }, (_, index) => index + 1).map(
              (number) => (
                <div
                  key={number}
                  onClick={() => handleClick(number)}
                  className={`cursor-pointer ${
                    number === currentPage ? 'font-bold underline' : ''
                  }`}
                >
                  {number}
                </div>
              )
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default SingelCard
